import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, User, Heart } from "lucide-react";
import { useCartStore, useWishlistStore, useAuthStore } from "@/lib/store";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const [announcementHeight, setAnnouncementHeight] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { getItemCount } = useCartStore();
  const { items: wishlistItems } = useWishlistStore();
  const { isAuthenticated, user, logout } = useAuthStore();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Calculate announcement bar height
  useEffect(() => {
    const updateAnnouncementHeight = () => {
      const announcementBar = document.querySelector('[data-announcement-bar]');
      if (announcementBar) {
        setAnnouncementHeight(announcementBar.clientHeight);
      } else {
        setAnnouncementHeight(0);
      }
    };
    updateAnnouncementHeight();
    const interval = setInterval(updateAnnouncementHeight, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Determine active section based on scroll position
      if (location.pathname === "/") {
        const sections = ["shop-by-collection", "shop-by-category", "why-aarsa", "reviews"];
        const scrollPosition = window.scrollY + 100; // Offset for header
        
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              setActiveSection(sectionId);
              return;
            }
          }
        }
        
        // If at top and no hash in URL, set Home as active
        if (window.scrollY < 200 && !location.hash) {
          setActiveSection("home");
        } else if (window.scrollY < 200 && location.hash) {
          // If there's a hash but we're at top, keep the hash section active
          const hash = location.hash.substring(1);
          setActiveSection(hash);
        }
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check
    
    // Check hash on mount
    if (location.hash) {
      const hash = location.hash.substring(1);
      setActiveSection(hash);
      setTimeout(() => {
        scrollToSection(hash);
      }, 100);
    } else if (location.pathname === "/") {
      setActiveSection("home");
    }
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location]);

  const scrollToSection = (sectionId: string) => {
    // Wait a bit for DOM to be ready, especially after navigation
    const attemptScroll = (attempt = 0) => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Calculate total header height (announcement bar + header)
        const announcementBar = document.querySelector('[data-announcement-bar]');
        const announcementHeight = announcementBar ? announcementBar.clientHeight : 0;
        const headerElement = document.querySelector('header');
        const headerHeight = headerElement ? headerElement.clientHeight : 80;
        const totalOffset = announcementHeight + headerHeight + 20; // 20px extra padding
        
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - totalOffset;

        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: "smooth",
        });
      } else if (attempt < 5) {
        // Retry if element not found (might still be loading)
        setTimeout(() => attemptScroll(attempt + 1), 100);
      }
    };
    
    setTimeout(() => attemptScroll(), 200); // Increased delay for mobile
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => {
    e.preventDefault();
    
    // Close mobile menu first
    setIsMobileMenuOpen(false);
    
    if (href === "/") {
      if (location.pathname === "/") {
        // Already on home page, scroll to top
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection("home");
        window.history.replaceState(null, "", "/");
      } else {
        // Navigate to home
        navigate("/");
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
          setActiveSection("home");
        }, 100);
      }
      return;
    }
    
    // Extract hash from href (e.g., "/#shop-by-collection" -> "shop-by-collection")
    const hash = href.includes("#") ? href.split("#")[1] : "";
    
    if (location.pathname !== "/") {
      // Navigate to home first, then scroll
      navigate("/");
      // Wait longer for navigation to complete on mobile
      setTimeout(() => {
        setActiveSection(hash);
        scrollToSection(hash);
        // Update URL hash after navigation
        if (hash) {
          window.history.replaceState(null, "", `/#${hash}`);
        }
      }, 300); // Increased delay for mobile navigation
    } else {
      // Already on home page, just scroll
      setActiveSection(hash);
      // Small delay to ensure menu is closed and DOM is ready
      setTimeout(() => {
        scrollToSection(hash);
        // Update URL hash
        if (hash) {
          window.history.replaceState(null, "", `/#${hash}`);
        }
      }, 100);
    }
  };

  const navItems = [
    { name: "Home", href: "/", id: "home" },
    { name: "Shop by Category", href: "/#shop-by-category", id: "shop-by-category" },
    { name: "Shop by Collection", href: "/#shop-by-collection", id: "shop-by-collection" },
    { name: "Why Aarsa", href: "/#why-aarsa", id: "why-aarsa" },
    { name: "Reviews", href: "/#reviews", id: "reviews" },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg"
          : "bg-transparent"
      }`}
      style={{ top: `${announcementHeight}px` }}
    >
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === "/") {
                // Already on home page, scroll to top
                window.scrollTo({ top: 0, behavior: "smooth" });
                setActiveSection("home");
                // Clear hash if present
                window.history.replaceState(null, "", "/");
              } else {
                // Navigate to home page
                navigate("/");
                // Scroll to top after navigation
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  setActiveSection("home");
                }, 100);
              }
              // Close mobile menu if open
              setIsMobileMenuOpen(false);
            }}
            className="flex items-center space-x-2"
            aria-label="Go to home"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative h-14 w-auto"
            >
              <img
                src="/aarsalogo.png"
                alt="Aarsa Logo"
                className="h-14 w-auto object-contain"
                style={{ width: "auto", height: "56px" }}
              />
            </motion.div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => {
              // Only check if activeSection matches item.id
              const isActive = activeSection === item.id;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`group relative inline-flex flex-col items-center text-sm font-medium px-4 py-2 transition-all duration-300 ${
                    isActive
                      ? "text-gray-800 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <span>{item.name}</span>
                  {/* Active indicator - small line */}
                  {isActive && (
                    <motion.div
                      className="mt-1 w-6 h-0.5 bg-gray-600"
                      layoutId="activeNavLine"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            <Link to="/wishlist">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
                aria-label="Wishlist"
              >
                <Heart className="w-5 h-5" />
                {mounted && wishlistItems.length > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-aarsa-sage-600 text-white text-xs rounded-full flex items-center justify-center">
                    {wishlistItems.length}
                  </span>
                )}
              </motion.button>
            </Link>
            <Link to="/cart">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
                aria-label="Cart"
              >
                <ShoppingBag className="w-5 h-5" />
                {mounted && getItemCount() > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-aarsa-sage-600 text-white text-xs rounded-full flex items-center justify-center">
                    {getItemCount()}
                  </span>
                )}
              </motion.button>
            </Link>
                {isAuthenticated ? (
                  <Link to="/account">
                    <button
                      type="button"
                      className="group relative hidden md:flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-aarsa-sage-600 to-aarsa-sage-700 text-white text-sm font-medium shadow-lg hover:shadow-xl hover:from-aarsa-sage-700 hover:to-aarsa-sage-800 transition-all cursor-pointer overflow-hidden"
                    >
                      {/* Shine Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      <User className="w-4 h-4 relative z-10" />
                      <span className="relative z-10">{user?.first_name || "Account"}</span>
                    </button>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowLoginDialog(true)}
                    className="group relative hidden md:flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-aarsa-sage-600 to-aarsa-sage-700 text-white text-sm font-medium shadow-lg hover:shadow-xl hover:from-aarsa-sage-700 hover:to-aarsa-sage-800 transition-all cursor-pointer overflow-hidden"
                  >
                    {/* Shine Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    <User className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Login</span>
                  </button>
                )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <div className="container mx-auto px-4 py-4 space-y-1">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.name}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(e, item.href);
                    }}
                    className={`relative flex flex-col items-center py-3 px-4 transition-all duration-200 w-full text-left ${
                      isActive
                        ? "text-gray-800 font-semibold"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <span>{item.name}</span>
                    {/* Active indicator - small line */}
                    {isActive && (
                      <motion.div
                        className="mt-1 w-6 h-0.5 bg-gray-600"
                        layoutId="activeNavLineMobile"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </button>
                );
              })}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/account"
                    className="block py-2 text-gray-700 hover:text-gray-900"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 text-red-600 hover:text-red-700"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginDialog(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-gray-700 hover:text-gray-900"
                >
                  Login
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Dialog */}
      <OTPLoginDialog isOpen={showLoginDialog} onClose={() => setShowLoginDialog(false)} />
    </motion.header>
  );
}
